import { get } from 'lodash'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'

import {
  setActivePatientEnrolledProgram,
  setDiagnosisList,
  setDiagnosisLoading,
  setFollowUpCall,
  setPatientDiagnosis,
  setPatientDiagnosisLoading,
  setPatientDiseases,
  setPatientDiseasesLoading,
  setPatientMedicalDevices,
  setPatientOnHoldHistory,
  setPatientRpmBillableHistory
} from 'src/store/modules/actionCreators'
import { updatePatientList } from 'src/store/modules/admin/activityLog'
import {
  getPatientBillingStatistics,
  patientUuidSelector
} from 'src/store/modules/patient'
import { getPatientProfile } from 'src/store/modules/patient/profile'
import { setPatientEnrollments } from 'src/store/modules/patient/program'

import {
  COMPANY_ATTACHMENT_DELETE_PATCH,
  COMPANY_ATTACHMENT_POST,
  CREATE_MEDICAL_DEVICE,
  DATE_FORMAT_FOR_SERVER,
  DELETE_FOLLOW_UP_CALL,
  DELETE_MEDICAL_DEVICE,
  DELETE_PATIENT_PROGRAM_CONSENT_DOCUMENT,
  EDIT_MEDICAL_DEVICE,
  EXTERNAL_REFERRAL,
  EXTERNAL_REFERRAL_ATTACHMENTS_DELETE,
  EXTERNAL_REFERRAL_ATTACHMENTS_UPLOAD,
  EXTERNAL_REFERRAL_CREATE,
  EXTERNAL_REFERRAL_DELETE,
  EXTERNAL_REFERRAL_UPDATE,
  FOLLOW_UP_CALL,
  GET_ICD,
  GET_PATIENT_ENROLLMENT,
  GET_PATIENT_PROGRAM_CONSENT_DOCUMENT,
  PATCH_PATIENT_ENROLLMENT,
  PATCH_PATIENT_PROGRAM,
  PATCH_PATIENT_RPM_BILLABLE,
  PATIENT_CONSENT_DATE,
  PATIENT_DIAGNOSIS,
  PATIENT_DISEASES,
  PATIENT_ENROLLED_PROGRAM_HOLD,
  PATIENT_ENROLLED_PROGRAM_ON_HOLD_HISTORY,
  PATIENT_ENROLLED_PROGRAM_RESUME,
  PATIENT_ENROLLED_PROGRAM_RPM_BILLABLE_HISTORY,
  PATIENT_ENROLLED_PROGRAM_VALIDATE_COCM,
  PATIENT_ENROLLED_PROGRAM_VALIDATE_RPM,
  PATIENT_ENROLLED_PROGRAM_VALIDATE_RTM,
  PATIENT_MEDICAL_DEVICES,
  PATIENT_PROGRAM_ROLLBACK_POST,
  PATIENT_SUB_PROGRAM_ROLLBACK_POST,
  POST_PATIENT_ENROLLMENT,
  POST_PATIENT_ENROLLMENT_DISCHARGE,
  POST_PATIENT_PROGRAM,
  POST_PATIENT_PROGRAM_DISCHARGE,
  REFERRAL_SERVICE_PROVIDER,
  REFERRAL_SERVICE_PROVIDER_EDIT
} from 'src/constants'
import api from 'src/services/@core/API'

const prepareProgramData = (fields) => {
  const preparedObj = {}
  Object.keys(fields).forEach((key) => {
    switch (key) {
      case 'reason':
      case 'type':
        preparedObj[key] = { uuid: fields[key] }
        break

      case 'ccmBillingProgram':
      case 'rpmBillingProgram':
      case 'rtmBillingProgram':
      case 'attachment':
        preparedObj[key] = fields[key]
        break

      case 'primaryDisease':
        preparedObj[key] = { uuid: fields[key] }
        break

      case 'startDate':
      case 'endPeriod':
      case 'startPeriod':
      case 'consentDate':
        if (!fields[key]) break
        preparedObj[key] = fields[key].format(DATE_FORMAT_FOR_SERVER)
        break

      case 'endDate':
        preparedObj[key] = !fields[key]
          ? fields[key]
          : fields[key].format(DATE_FORMAT_FOR_SERVER)
        break

      default:
        break
    }
  })

  return preparedObj
}

export const fetchPatientEnrollments =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, query = {} } = config

    try {
      const { items } = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_PATIENT_ENROLLMENT,
          params,
          query
        }
      })

      dispatch(setPatientEnrollments(items))
    } catch (err) {
      catchError(err)
    }
  }

export const savePatientEnrollment =
  (config = {}, patientList = []) =>
  async (dispatch) => {
    const { params = {}, payload = {} } = config

    try {
      const patientUuid = get(params, ['patientUuid'], null)
      await httpClient.request({
        method: 'post',
        url: {
          pathname: POST_PATIENT_ENROLLMENT,
          params
        },
        payload
      })

      const profile = await getPatientProfile(patientUuid)

      await Promise.all([
        dispatch(getActiveProgram()),
        dispatch(updatePatientList(profile, patientList)),
        dispatch(getPatientBillingStatistics(patientUuid))
      ])

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const updatePatientEnrollment =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, payload = {} } = config

    try {
      const patientUuid = get(params, ['patientUuid'], null)
      await httpClient.request({
        method: 'patch',
        url: {
          pathname: PATCH_PATIENT_ENROLLMENT,
          params
        },
        payload
      })

      await Promise.all([
        dispatch(getActiveProgram()),
        dispatch(getPatientBillingStatistics(patientUuid))
      ])

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const dischargePatientEnrollment =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: POST_PATIENT_ENROLLMENT_DISCHARGE,
          params
        },
        payload
      })

      await dispatch(getActiveProgram())

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }
export const savePatientProgram =
  (config = {}, patientUuid = '', patientList = []) =>
  async (dispatch) => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: POST_PATIENT_PROGRAM,
          params
        },
        payload
      })

      const profile = await getPatientProfile(patientUuid)

      await Promise.all([
        dispatch(getActiveProgram()),
        dispatch(updatePatientList(profile, patientList)),
        dispatch(getPatientBillingStatistics(patientUuid))
      ])

      return true
    } catch (err) {
      catchError(err, { ignoredValuesList: ['startDate', 'endDate'] })

      return false
    }
  }

export const updatePatientProgram =
  (config = {}, patientUuid = '') =>
  async (dispatch) => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'patch',
        url: {
          pathname: PATCH_PATIENT_PROGRAM,
          params
        },
        payload
      })

      await Promise.all([
        dispatch(getActiveProgram()),
        dispatch(getPatientBillingStatistics(patientUuid))
      ])

      return true
    } catch (err) {
      catchError(err, { ignoredValuesList: ['startDate', 'endDate'] })

      return false
    }
  }

export const dischargePatientProgram =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: POST_PATIENT_PROGRAM_DISCHARGE,
          params
        },
        payload
      })

      dispatch(getActiveProgram())

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const getActiveProgram = () => (dispatch, getState) => {
  const patientUuid = patientUuidSelector(getState())

  if (!patientUuid) {
    const errorReason = !patientUuid ? 'User id' : 'Data'
    return Promise.reject(new Error(`${errorReason} not available`))
  }

  return api.getActiveEnrolledProgram({ id: patientUuid }).then(
    (program) => {
      dispatch(setActivePatientEnrolledProgram(program.enrolledProgram))
    },
    (err) => Promise.reject(err)
  )
}

export const setPatientEnrolledProgramOnHold =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, payload = {} } = config

    try {
      const isUpdated = await httpClient.request({
        method: 'post',
        url: {
          pathname: PATIENT_ENROLLED_PROGRAM_HOLD,
          params
        },
        payload
      })

      const patientEnrollmentsConfig = {
        params: {
          patientUuid: params?.patientUuid || null
        },
        query: {
          sort: 'startDate:desc'
        }
      }
      await dispatch(fetchPatientEnrollments(patientEnrollmentsConfig))

      return isUpdated
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const setPatientEnrolledProgramResume =
  (config = {}) =>
  async (dispatch) => {
    const { params = {} } = config

    try {
      const isUpdated = await httpClient.request({
        method: 'post',
        url: {
          pathname: PATIENT_ENROLLED_PROGRAM_RESUME,
          params
        }
      })

      const patientEnrollmentsConfig = {
        params: {
          patientUuid: params?.patientUuid || null
        },
        query: {
          sort: 'startDate:desc'
        }
      }
      await dispatch(fetchPatientEnrollments(patientEnrollmentsConfig))

      return isUpdated
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const uploadConsentDocument =
  (config = {}) =>
  async () => {
    const { params = {}, data } = config

    try {
      const res = await httpClient.upload({
        url: {
          pathname: COMPANY_ATTACHMENT_POST,
          params
        },
        data
      })

      return res
    } catch (err) {
      catchError(err)
    }
  }

export const deleteConsentDocumentByUuid =
  (config = {}) =>
  async () => {
    const { params } = config

    try {
      await httpClient.request({
        method: 'delete',
        url: {
          pathname: COMPANY_ATTACHMENT_DELETE_PATCH,
          params
        }
      })
    } catch (err) {
      catchError(err)
    }
  }

export const deleteConsentDocument =
  (config = {}) =>
  async () => {
    const { params } = config

    try {
      await httpClient.request({
        method: 'delete',
        url: {
          pathname: DELETE_PATIENT_PROGRAM_CONSENT_DOCUMENT,
          params
        }
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const downloadConsentDocument =
  (config = {}) =>
  async () => {
    const { params } = config

    try {
      const { data, headers } = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_PATIENT_PROGRAM_CONSENT_DOCUMENT,
          params
        },
        isDownload: true
      })
      const mappedHeaders = {}
      Object.keys(headers).forEach((key) => {
        mappedHeaders[key.toLocaleLowerCase()] = headers[key]
      })
      const contentDisposition = get(mappedHeaders, 'content-disposition', '')
      const fileName = contentDisposition
        .slice(contentDisposition.indexOf('filename') + 9)
        .replaceAll('"', '')
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url

      link.setAttribute('download', fileName)

      document.body.appendChild(link)

      link.click()
    } catch (err) {
      catchError(err)
    }
  }

export const setConsentDate = (uuid, payload) => async (dispatch) => {
  try {
    await httpClient.request({
      method: 'post',
      url: {
        pathname: PATIENT_CONSENT_DATE,
        params: {
          uuid
        }
      },
      payload
    })

    dispatch(getPatientProfile(uuid))
  } catch (err) {
    catchError(err)
  }
}

export const getPatientDiseases = (uuid) => async (dispatch) => {
  dispatch(setPatientDiseasesLoading(true))

  try {
    const diseases = await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENT_DISEASES,
        params: {
          uuid
        }
      }
    })

    dispatch(setPatientDiseases(diseases))

    return diseases
  } catch (err) {
    catchError(err)
  } finally {
    dispatch(setPatientDiseasesLoading(false))
  }
}

export const getPatientDiagnosis = (uuid) => async (dispatch) => {
  dispatch(setPatientDiagnosisLoading(true))
  try {
    const diagnosis = await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENT_DIAGNOSIS,
        params: {
          uuid
        }
      }
    })
    dispatch(setPatientDiagnosis(diagnosis))
  } catch (err) {
    catchError(err)
  } finally {
    dispatch(setPatientDiagnosisLoading(false))
  }
}

export const getFollowUpCall = (uuid, isFromModal) => async (dispatch) => {
  try {
    const { items } = await httpClient.request({
      method: 'get',
      url: {
        pathname: FOLLOW_UP_CALL,
        params: {
          uuid
        }
      }
    })

    if (!isFromModal) {
      dispatch(setFollowUpCall(items[0] || {}))
    }

    return items[0] || {}
  } catch (err) {
    catchError(err)
  }
}

export const postFollowUpCall = (uuid, payload) => async (dispatch) => {
  try {
    await httpClient.request({
      method: 'post',
      url: {
        pathname: FOLLOW_UP_CALL,
        params: {
          uuid
        }
      },
      payload
    })

    return dispatch(getFollowUpCall(uuid))
  } catch (err) {
    catchError(err)
  }
}

export const removeFollowUp = (uuid) => async (dispatch) => {
  try {
    await httpClient.request({
      method: 'delete',
      url: {
        pathname: DELETE_FOLLOW_UP_CALL,
        params: {
          uuid
        }
      }
    })

    return dispatch(getFollowUpCall(uuid))
  } catch (err) {
    catchError(err)
  }
}

export const getPatientMedicalDevices =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, query = {} } = config

    try {
      const devices = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_MEDICAL_DEVICES,
          params,
          query
        }
      })
      dispatch(setPatientMedicalDevices(devices))
    } catch (err) {
      catchError(err)
    }
  }

export const createPatientMedicalDevices =
  (config = {}) =>
  async (dispatch) => {
    const { payload = {} } = config
    const patientUuid = get(payload, ['patient', 'uuid'], null)

    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: CREATE_MEDICAL_DEVICE
        },
        payload
      })
      const config = {
        params: {
          patientUuid
        }
      }

      dispatch(getPatientMedicalDevices(config))

      return false
    } catch (err) {
      catchError(err)

      return true
    }
  }

export const updatePatientMedicalDevice =
  (payload, uuid) => async (dispatch) => {
    const {
      patient: { uuid: patientUuid }
    } = payload

    try {
      await httpClient.request({
        method: 'patch',
        url: {
          pathname: EDIT_MEDICAL_DEVICE,
          params: { uuid }
        },
        payload
      })
      const config = {
        params: {
          patientUuid
        }
      }

      dispatch(getPatientMedicalDevices(config))

      return false
    } catch (err) {
      catchError(err)

      return true
    }
  }

export const deletePatientMedicalDevice =
  (uuid, patientUuid) => async (dispatch) => {
    try {
      await httpClient.request({
        method: 'delete',
        url: {
          pathname: DELETE_MEDICAL_DEVICE,
          params: {
            uuid
          }
        }
      })
      const config = {
        params: {
          patientUuid
        }
      }

      dispatch(getPatientMedicalDevices(config))
    } catch (err) {
      catchError(err)
    }
  }

export const getIcdList = async (config = {}) => {
  const { query = {} } = config

  try {
    const { items } = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_ICD,
        query: { ...query, 'per-page': 100, page: 1 }
      }
    })

    return items
  } catch (err) {
    catchError(err)

    return []
  }
}

export const fetchIcdList =
  (config = {}) =>
  async (dispatch) => {
    dispatch(setDiagnosisLoading(true))

    const items = await getIcdList(config)

    dispatch(setDiagnosisList(items))
    dispatch(setDiagnosisLoading(false))
  }

export const validateProgramByRPM =
  (config = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      const response = await httpClient.request({
        method: 'post',
        url: {
          pathname: PATIENT_ENROLLED_PROGRAM_VALIDATE_RPM,
          params
        },
        payload: prepareProgramData(payload)
      })

      return response
    } catch (err) {
      catchError(err)

      return err
    }
  }

export const validateProgramByRTM =
  (config = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      const response = await httpClient.request({
        method: 'post',
        url: {
          pathname: PATIENT_ENROLLED_PROGRAM_VALIDATE_RTM,
          params
        },
        payload: prepareProgramData(payload)
      })

      return response
    } catch (err) {
      catchError(err)

      return err
    }
  }
export const validateProgramByCoCM =
  (config = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      const response = await httpClient.request({
        method: 'post',
        url: {
          pathname: PATIENT_ENROLLED_PROGRAM_VALIDATE_COCM,
          params
        },
        payload: prepareProgramData(payload)
      })

      return response
    } catch (err) {
      catchError(err)

      return err
    }
  }

export const fetchPatientOnHoldHistory =
  (config = {}) =>
  async (dispatch) => {
    try {
      const items = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_ENROLLED_PROGRAM_ON_HOLD_HISTORY,
          ...config
        }
      })

      dispatch(setPatientOnHoldHistory(items))
    } catch (err) {
      catchError(err)

      return err
    }
  }

export const updatePatientRPMBillable =
  (config = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'patch',
        url: {
          pathname: PATCH_PATIENT_RPM_BILLABLE,
          params
        },
        payload
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const fetchPatientRPMBillableHistory =
  (config = {}) =>
  async (dispatch) => {
    try {
      const items = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_ENROLLED_PROGRAM_RPM_BILLABLE_HISTORY,
          ...config
        }
      })

      dispatch(setPatientRpmBillableHistory(items))
    } catch (err) {
      catchError(err)

      return err
    }
  }

export const rollbackEnrolledProgram =
  (config = {}) =>
  async () => {
    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: PATIENT_PROGRAM_ROLLBACK_POST,
          ...config
        }
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const rollbackEnrolledSubProgram =
  (config = {}) =>
  async () => {
    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: PATIENT_SUB_PROGRAM_ROLLBACK_POST,
          ...config
        }
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const externalReferral = async (uuid) => {
  try {
    const data = await httpClient.request({
      method: 'get',
      url: {
        pathname: EXTERNAL_REFERRAL,
        params: {
          patientUuid: uuid
        }
      }
    })

    return data?.items
  } catch (err) {
    catchError(err)
  }
}

export const getReferralServiceProvider = async (data = {}) => {
  try {
    const response = await httpClient.request({
      method: 'get',
      url: {
        pathname: REFERRAL_SERVICE_PROVIDER,
        query: data?.query || {}
      }
    })

    return response?.items
  } catch (err) {
    catchError(err)
  }
}

export const editReferralServiceProvider = async (data) => {
  try {
    await httpClient.request({
      method: 'patch',
      url: {
        pathname: REFERRAL_SERVICE_PROVIDER_EDIT,
        params: data?.params
      },
      payload: data?.payload || {}
    })

    return true
  } catch (err) {
    catchError(err)
  }
}

export const createReferralServiceProvider = async (data) => {
  try {
    return await httpClient.request({
      method: 'post',
      url: {
        pathname: REFERRAL_SERVICE_PROVIDER,
        params: data?.params
      },
      payload: data?.payload || {}
    })
  } catch (err) {
    catchError(err)
    return false
  }
}

export const externalReferralAttachmentsUpload = async (data) => {
  try {
    await httpClient.request({
      method: 'patch',
      url: {
        pathname: EXTERNAL_REFERRAL_ATTACHMENTS_UPLOAD,
        params: data?.params
      },
      payload: data?.payload || {}
    })

    return true
  } catch (err) {
    catchError(err)
  }
}

export const updateExternalReferral = async (data) => {
  try {
    await httpClient.request({
      method: 'patch',
      url: {
        pathname: EXTERNAL_REFERRAL_UPDATE,
        params: data?.params
      },
      payload: data?.payload || {}
    })

    return true
  } catch (err) {
    catchError(err)
  }
}

export const createExternalReferral = async (data) => {
  try {
    await httpClient.request({
      method: 'post',
      url: {
        pathname: EXTERNAL_REFERRAL_CREATE,
        params: data?.params
      },
      payload: data?.payload || {}
    })

    return true
  } catch (err) {
    catchError(err)
  }
}

export const externalReferralAttachmentsDelete = async (params) => {
  try {
    await httpClient.request({
      method: 'delete',
      url: {
        pathname: EXTERNAL_REFERRAL_ATTACHMENTS_DELETE,
        params
      }
    })

    return true
  } catch (err) {
    catchError(err)
  }
}

export const deleteExternalReferral = async (params) => {
  try {
    await httpClient.request({
      method: 'delete',
      url: {
        pathname: EXTERNAL_REFERRAL_DELETE,
        params
      }
    })

    return true
  } catch (err) {
    catchError(err)
  }
}
